import { useEffect, useState } from 'react';
import Home from './screens/Home';
import AddEntry from './screens/AddEntry';
import Exchange from './screens/Exchange';
import History from './screens/History';
import Reports from './screens/Reports';
import StartingBalance from './screens/StartingBalance';
import Reconcile from './screens/Reconcile';
import CashCounter from './screens/CashCounter';
import Settings from './screens/Settings';
import Categories from './screens/Categories';
import Presets from './screens/Presets';
import RecurringScreen from './screens/Recurring';
import Budgets from './screens/Budgets';
import Debts from './screens/Debts';
import Goals from './screens/Goals';
import { getInitData, tg } from './lib/telegram';
import type { Entry } from './lib/types';

const BOT_USERNAME = 'MoneyTrackerBBot';

export type Screen =
  | { name: 'home' }
  | { name: 'add'; kind: 'income' | 'expense' }
  | { name: 'edit'; entry: Entry }
  | { name: 'exchange' }
  | { name: 'history' }
  | { name: 'reports' }
  | { name: 'starting-balance' }
  | { name: 'reconcile' }
  | { name: 'cash-counter'; from?: 'home' | 'settings' }
  | { name: 'settings' }
  | { name: 'categories' }
  | { name: 'presets' }
  | { name: 'recurring' }
  | { name: 'budgets' }
  | { name: 'debts' }
  | { name: 'goals' };

export default function App() {
  const [screen, setScreen] = useState<Screen>({ name: 'home' });
  const [online, setOnline] = useState(navigator.onLine);
  const hasInitData = getInitData().length > 0;

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  if (!hasInitData) {
    return (
      <div className="app">
        <div className="header"><h1>MoneyTrack</h1></div>
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Open this from Telegram</h2>
          <p className="muted">
            MoneyTrack uses your Telegram account to identify you and keep your data private.
            Open it from inside the bot, not in a regular browser.
          </p>
          <a
            className="btn"
            href={`https://t.me/${BOT_USERNAME}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginTop: 12 }}
          >
            Open @{BOT_USERNAME}
          </a>
          <p className="muted small" style={{ marginTop: 12, marginBottom: 0 }}>
            Then tap the menu button (☰) next to the message box.
          </p>
        </div>
      </div>
    );
  }

  // Wire Telegram BackButton to navigate back to home.
  useEffect(() => {
    const back = tg()?.BackButton;
    if (!back) return;
    const handler = () => setScreen({ name: 'home' });
    if (screen.name === 'home') {
      back.hide();
    } else {
      back.show();
      back.onClick(handler);
    }
    return () => back.offClick(handler);
  }, [screen.name]);

  return (
    <div className="app">
      {!online && (
        <div className="offline-banner">⚠ Offline — showing last-saved data; changes can't be saved</div>
      )}
      {screen.name === 'home' && <Home navigate={setScreen} />}
      {screen.name === 'add' && (
        <AddEntry kind={screen.kind} onDone={() => setScreen({ name: 'home' })} onBack={() => setScreen({ name: 'home' })} />
      )}
      {screen.name === 'edit' && (
        <AddEntry
          kind={screen.entry.kind === 'income' ? 'income' : 'expense'}
          editing={screen.entry}
          onDone={() => setScreen({ name: 'home' })}
          onBack={() => setScreen({ name: 'home' })}
        />
      )}
      {screen.name === 'exchange' && (
        <Exchange onDone={() => setScreen({ name: 'home' })} onBack={() => setScreen({ name: 'home' })} />
      )}
      {screen.name === 'history' && <History onBack={() => setScreen({ name: 'home' })} navigate={setScreen} />}
      {screen.name === 'reports' && <Reports onBack={() => setScreen({ name: 'home' })} />}
      {screen.name === 'starting-balance' && (
        <StartingBalance onDone={() => setScreen({ name: 'home' })} onBack={() => setScreen({ name: 'home' })} />
      )}
      {screen.name === 'reconcile' && (
        <Reconcile onDone={() => setScreen({ name: 'home' })} onBack={() => setScreen({ name: 'home' })} />
      )}
      {screen.name === 'cash-counter' && (
        <CashCounter
          onDone={() => setScreen({ name: 'home' })}
          onBack={() => setScreen(screen.from === 'settings' ? { name: 'settings' } : { name: 'home' })}
        />
      )}
      {screen.name === 'settings' && <Settings onBack={() => setScreen({ name: 'home' })} navigate={setScreen} />}
      {screen.name === 'categories' && <Categories onBack={() => setScreen({ name: 'settings' })} />}
      {screen.name === 'presets' && <Presets onBack={() => setScreen({ name: 'settings' })} />}
      {screen.name === 'recurring' && <RecurringScreen onBack={() => setScreen({ name: 'settings' })} />}
      {screen.name === 'budgets' && <Budgets onBack={() => setScreen({ name: 'settings' })} />}
      {screen.name === 'debts' && <Debts onBack={() => setScreen({ name: 'settings' })} />}
      {screen.name === 'goals' && <Goals onBack={() => setScreen({ name: 'settings' })} />}

      <nav className="bottom-nav">
        <button className={screen.name === 'home' ? 'active' : ''} onClick={() => setScreen({ name: 'home' })}>
          <span className="nav-icon">🏠</span><span>Home</span>
        </button>
        <button className={screen.name === 'reports' ? 'active' : ''} onClick={() => setScreen({ name: 'reports' })}>
          <span className="nav-icon">📊</span><span>Reports</span>
        </button>
        <button className={screen.name === 'history' ? 'active' : ''} onClick={() => setScreen({ name: 'history' })}>
          <span className="nav-icon">🕘</span><span>History</span>
        </button>
      </nav>
    </div>
  );
}
